import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { FiCalendar, FiChevronLeft, FiChevronRight, FiX } from 'react-icons/fi';

/**
 * One input for a from/to date filter, with its own calendar popover — no
 * native <input type="date">.
 *
 * Values go in and out as YYYY-MM-DD strings and are never read through the
 * browser's time zone. The first click picks one end, the second click the
 * other (in either order) and commits the range; clicking the same day twice
 * gives a one-day range.
 *
 * `maxSpanDays` is the number of days a range may COVER (1st to 2nd = 2). Once
 * the first end is picked, days that would break it are greyed out.
 *
 * `single` turns it into a one-date picker that reports `(date, '')`.
 */

interface DateRangePickerProps {
  from: string;
  to: string;
  onChange: (from: string, to: string) => void;
  label?: string;
  placeholder?: string;
  maxSpanDays?: number;
  single?: boolean;
  className?: string;
}

const DAY_MS = 86_400_000;
const WEEK_DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const pad = (n: number) => String(n).padStart(2, '0');
const toKey = (year: number, month: number, day: number) => `${year}-${pad(month + 1)}-${pad(day)}`;

const parseKey = (value?: string | null): { year: number; month: number; day: number } | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value ?? '').trim());
  return match ? { year: Number(match[1]), month: Number(match[2]) - 1, day: Number(match[3]) } : null;
};

const dayNumber = (key: string): number => {
  const parts = parseKey(key);
  return parts ? Math.floor(Date.UTC(parts.year, parts.month, parts.day) / DAY_MS) : NaN;
};

const keyFromDayNumber = (value: number) => new Date(value * DAY_MS).toISOString().slice(0, 10);

const todayKey = () => {
  const now = new Date();
  return toKey(now.getFullYear(), now.getMonth(), now.getDate());
};

const formatKey = (key: string) => {
  const parts = parseKey(key);
  return parts ? `${pad(parts.day)} ${MONTHS[parts.month]} ${parts.year}` : '';
};

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  from,
  to,
  onChange,
  label,
  placeholder,
  maxSpanDays,
  single = false,
  className = '',
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [alignRight, setAlignRight] = useState(false);
  // The first end of a range, picked but not yet committed.
  const [anchor, setAnchor] = useState<string | null>(null);
  const [hovered, setHovered] = useState<string | null>(null);
  const [view, setView] = useState(() => {
    const start = parseKey(from) || parseKey(to) || parseKey(todayKey())!;
    return { year: start.year, month: start.month };
  });

  useEffect(() => {
    if (!open) return;
    const start = parseKey(from) || parseKey(to) || parseKey(todayKey())!;
    setView({ year: start.year, month: start.month });
    setAnchor(null);
    setHovered(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  // Open towards the left when the popover would run off the right edge.
  useLayoutEffect(() => {
    if (!open || !rootRef.current || !popoverRef.current) return;
    const rootLeft = rootRef.current.getBoundingClientRect().left;
    setAlignRight(rootLeft + popoverRef.current.offsetWidth > window.innerWidth - 8);
  }, [open]);

  const cells = useMemo(() => {
    const firstWeekDay = new Date(view.year, view.month, 1).getDay();
    const daysInMonth = new Date(view.year, view.month + 1, 0).getDate();
    const list: Array<string | null> = Array(firstWeekDay).fill(null);
    for (let day = 1; day <= daysInMonth; day++) list.push(toKey(view.year, view.month, day));
    return list;
  }, [view]);

  const years = useMemo(() => {
    const current = new Date().getFullYear();
    const list: number[] = [];
    for (let year = current - 10; year <= current + 2; year++) list.push(year);
    if (!list.includes(view.year)) list.push(view.year);
    return list.sort((a, b) => a - b);
  }, [view.year]);

  const today = todayKey();
  // While the second end is being chosen the highlight follows the pointer.
  const previewEnd = anchor ? hovered ?? anchor : null;
  const rangeStart = anchor ? (previewEnd! < anchor ? previewEnd! : anchor) : from;
  const rangeEnd = anchor ? (previewEnd! < anchor ? anchor : previewEnd!) : single ? from : to;

  const isDisabled = (key: string) =>
    Boolean(anchor && maxSpanDays && Math.abs(dayNumber(key) - dayNumber(anchor)) + 1 > maxSpanDays);

  const commit = (nextFrom: string, nextTo: string) => {
    onChange(nextFrom, nextTo);
    setAnchor(null);
    setHovered(null);
    setOpen(false);
  };

  const pickDay = (key: string) => {
    if (single) {
      commit(key, '');
      return;
    }
    if (!anchor) {
      setAnchor(key);
      setHovered(key);
      return;
    }
    if (isDisabled(key)) return;
    commit(key < anchor ? key : anchor, key < anchor ? anchor : key);
  };

  const shiftMonth = (delta: number) =>
    setView((prev) => {
      const next = new Date(prev.year, prev.month + delta, 1);
      return { year: next.getFullYear(), month: next.getMonth() };
    });

  const presets = useMemo(() => {
    if (single) return [{ label: 'Today', from: today, to: '' }];
    const todayNumber = dayNumber(today);
    const now = parseKey(today)!;
    const list = [
      { label: 'Today', from: today, to: today },
      { label: 'Last 7 days', from: keyFromDayNumber(todayNumber - 6), to: today },
      { label: 'Last 30 days', from: keyFromDayNumber(todayNumber - 29), to: today },
      { label: 'This month', from: toKey(now.year, now.month, 1), to: today },
    ];
    return list.filter(
      (preset) => !maxSpanDays || dayNumber(preset.to) - dayNumber(preset.from) + 1 <= maxSpanDays
    );
  }, [single, today, maxSpanDays]);

  const hasValue = Boolean(from || to);
  const display = single
    ? formatKey(from)
    : from && to
      ? from === to
        ? formatKey(from)
        : `${formatKey(from)} – ${formatKey(to)}`
      : from
        ? `From ${formatKey(from)}`
        : to
          ? `Until ${formatKey(to)}`
          : '';

  return (
    <div ref={rootRef} className={`relative block ${className}`}>
      {label && <span className="block text-xs font-medium text-gray-700 mb-1">{label}</span>}
      <div
        role="button"
        tabIndex={0}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setOpen((prev) => !prev);
          }
        }}
        className={`w-full flex items-center gap-2 px-3 py-2 border rounded text-sm bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-600 ${
          open ? 'border-blue-600 ring-2 ring-blue-600' : 'border-gray-300'
        }`}
      >
        <FiCalendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
        <span className={`flex-1 truncate ${display ? 'text-gray-900' : 'text-gray-400'}`}>
          {display || placeholder || (single ? 'Select date' : 'Select date range')}
        </span>
        {hasValue && (
          <button
            type="button"
            title="Clear"
            onClick={(event) => {
              event.stopPropagation();
              onChange('', '');
              setAnchor(null);
            }}
            className="p-0.5 text-gray-400 hover:text-gray-700 rounded flex-shrink-0"
          >
            <FiX className="w-4 h-4" />
          </button>
        )}
      </div>

      {open && (
        <div
          ref={popoverRef}
          role="dialog"
          className={`absolute z-50 mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg p-3 ${
            alignRight ? 'right-0' : 'left-0'
          }`}
        >
          <div className="flex items-center justify-between gap-1 mb-2">
            <button
              type="button"
              onClick={() => shiftMonth(-1)}
              className="p-1.5 rounded hover:bg-gray-100 text-gray-600"
              title="Previous month"
            >
              <FiChevronLeft className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-1">
              <select
                value={view.month}
                onChange={(event) => setView((prev) => ({ ...prev, month: Number(event.target.value) }))}
                className="text-sm font-medium text-gray-900 bg-transparent rounded px-1 py-1 hover:bg-gray-100 focus:outline-none cursor-pointer"
              >
                {MONTHS_LONG.map((name, index) => (
                  <option key={name} value={index}>{name}</option>
                ))}
              </select>
              <select
                value={view.year}
                onChange={(event) => setView((prev) => ({ ...prev, year: Number(event.target.value) }))}
                className="text-sm font-medium text-gray-900 bg-transparent rounded px-1 py-1 hover:bg-gray-100 focus:outline-none cursor-pointer"
              >
                {years.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={() => shiftMonth(1)}
              className="p-1.5 rounded hover:bg-gray-100 text-gray-600"
              title="Next month"
            >
              <FiChevronRight className="w-4 h-4" />
            </button>
          </div>

          <div className="grid grid-cols-7 mb-1">
            {WEEK_DAYS.map((day) => (
              <div key={day} className="h-7 flex items-center justify-center text-[11px] font-medium text-gray-500">
                {day}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7" onMouseLeave={() => anchor && setHovered(anchor)}>
            {cells.map((key, index) => {
              if (!key) return <div key={`blank-${index}`} className="h-8" />;
              const disabled = isDisabled(key);
              const isStart = Boolean(rangeStart) && key === rangeStart;
              const isEnd = Boolean(rangeEnd) && key === rangeEnd;
              const inRange = Boolean(rangeStart && rangeEnd) && key > rangeStart && key < rangeEnd;
              const isEdge = isStart || isEnd;
              return (
                <div
                  key={key}
                  className={`h-8 ${inRange ? 'bg-blue-50' : ''} ${
                    isEdge && rangeStart && rangeEnd && rangeStart !== rangeEnd
                      ? isStart
                        ? 'bg-gradient-to-r from-transparent to-blue-50'
                        : 'bg-gradient-to-l from-transparent to-blue-50'
                      : ''
                  }`}
                >
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => pickDay(key)}
                    onMouseEnter={() => anchor && !disabled && setHovered(key)}
                    className={`w-full h-8 rounded text-sm transition-colors ${
                      disabled
                        ? 'text-gray-300 cursor-not-allowed'
                        : isEdge
                          ? 'bg-blue-800 text-white font-medium'
                          : inRange
                            ? 'text-blue-900 hover:bg-blue-100'
                            : 'text-gray-700 hover:bg-gray-100'
                    } ${key === today && !isEdge ? 'ring-1 ring-inset ring-blue-400' : ''}`}
                  >
                    {Number(key.slice(8))}
                  </button>
                </div>
              );
            })}
          </div>

          <div className="mt-2 pt-2 border-t border-gray-100">
            {!single && (
              <p className="text-[11px] text-gray-500 mb-2">
                {anchor
                  ? `Now pick the other end${maxSpanDays ? ` (up to ${maxSpanDays} days)` : ''}.`
                  : `Pick the first date${maxSpanDays ? ` — a range can cover up to ${maxSpanDays} days` : ''}.`}
              </p>
            )}
            <div className="flex flex-wrap gap-1">
              {presets.map((preset) => (
                <button
                  key={preset.label}
                  type="button"
                  onClick={() => commit(preset.from, preset.to)}
                  className="px-2 py-1 text-xs border border-gray-200 rounded text-gray-700 hover:bg-gray-50"
                >
                  {preset.label}
                </button>
              ))}
              {hasValue && (
                <button
                  type="button"
                  onClick={() => commit('', '')}
                  className="px-2 py-1 text-xs rounded text-red-600 hover:bg-red-50 ml-auto"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
